'use client'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { useState } from 'react'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`legal-tabpanel-${index}`}
      aria-labelledby={`legal-tab-${index}`}
    >
      {value === index && <Box py={3}>{children}</Box>}
    </div>
  )
}

export default function TermsOfServicePage() {
  const [value, setValue] = useState(0)

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Container maxWidth='lg'>
      <Box py={4}>
        <Typography variant='h4' component='h1' gutterBottom>
          Legal Documents
        </Typography>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          Effective Date: 1st September 2025
        </Typography>

        <Typography variant='body1' paragraph>
          Platform: Polar Tensor | Trading Engine: Polar One
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant='scrollable'
            scrollButtons='auto'
            aria-label='legal documents tabs'
          >
            <Tab label='Terms & Conditions' />
            <Tab label='AML & KYC' />
            <Tab label='Sanctions Policy' />
            <Tab label='Payment Services' />
            <Tab label='Risk Disclosure' />
            <Tab label='AI Trading Consent' />
            <Tab label='Fees & Tiers' />
            <Tab label='Privacy Policy' />
            <Tab label='Master Agreement' />
            <Tab label='Custody Agreement' />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <Typography variant='h5' gutterBottom>
            Global Terms & Conditions
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Investment Provider: POLAR TENSOR CORP. (Panama) | Payment Processor:
            POLAR MSB INC. (Montana, USA) | Technology Provider: POLAR TENSOR LAB
            LIMITED (Hong Kong)
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Introduction
              </Typography>
              <Typography variant='body1' paragraph>
                These Global Terms & Conditions (&quot;Terms&quot;) govern the access
                to and use of all services (&quot;Services&quot;) offered by POLAR
                TENSOR CORP., a corporation formed under the Laws on Corporations of
                the Republic of Panama (&quot;Company&quot;), through the digital
                asset platform known as Polar Tensor (&quot;Platform&quot;).
              </Typography>
              <Typography variant='body1' paragraph>
                By creating an account, you agree to be legally bound by these Terms
                and all related documents, including Master Client Investment
                Agreement, Risk Disclosure Statement, Algorithmic/AI Trading Consent,
                Custody & Third-Party Risk Agreement, Fee & Tier Schedule, Privacy
                Policy, and AML/KYC Policy.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Legal & Corporate Structure
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR CORP. (Panama):</strong> Provides virtual asset
                services, manages Trading Wallet funds, operates the Platform, and
                executes trades via the Polar One algorithmic trading system.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR MSB INC. (Montana, USA):</strong> A registered Money
                Services Business responsible exclusively for payment processing,
                including crypto/fiat deposits and withdrawals.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR LAB LIMITED (Hong Kong):</strong> Provides
                technology development, AI model development, system maintenance, and
                support. Does not provide investment services.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Eligibility & Access
              </Typography>
              <Typography variant='body1' paragraph>
                Clients must be at least 18 years old. The Company does not solicit
                or advertise services in regulated jurisdictions. Access is strictly
                based on reverse solicitation - the Client initiates contact
                voluntarily. Clients from sanctioned jurisdictions or individuals on
                sanctions lists are prohibited.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Services Provided
              </Typography>
              <Typography variant='body1'>
                Through Polar Tensor, the Company provides:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Digital asset deposit and withdrawal tools
                </Typography>
                <Typography component='li' variant='body1'>
                  Internal USDT wallet transfers
                </Typography>
                <Typography component='li' variant='body1'>
                  Access to the Polar One algorithmic trading system
                </Typography>
                <Typography component='li' variant='body1'>
                  Trading Wallet performance reporting
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Trading Wallet & AI Trading
              </Typography>
              <Typography variant='body1' paragraph>
                Transferring assets into the Trading Wallet constitutes authorization
                for discretionary trading using Polar One. The Client acknowledges:
                Polar One uses automated, algorithmic, and AI-driven trading models;
                trades execute at high frequency; digital asset trading involves
                substantial risk; you may lose all invested amounts; no returns are
                guaranteed; past performance does not predict future returns.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Fees & Costs
              </Typography>
              <Typography variant='body1' paragraph>
                Performance fees apply on net realized profits according to your
                tier. A withdrawal fee (0-12 months: 10%, 12-24 months: 5%, 24+
                months: free of charge) applies to withdrawals of initial principal
                and reinvested profits. Pure net profits not reinvested may be
                withdrawn without fees. Network and third-party fees are borne by the
                Client.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Deposits, Withdrawals & Transfers
              </Typography>
              <Typography variant='body1' paragraph>
                Supported deposits: BTC, USDT, USDC, ETH, SOL. Supported withdrawals:
                USDT, USDC. Internal transfers: USDT only. The Company is not
                responsible for assets sent to incorrect addresses. Withdrawals may
                be delayed due to compliance checks, custodian issues, or security
                reviews.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                8. Custody & Third-Party Risk
              </Typography>
              <Typography variant='body1' paragraph>
                Digital assets may be held with Binance or similar custodians
                operating independently. Downtime, hacks, insolvency, or operational
                failures may result in losses. The Company is not liable for
                custodian failures. Assets may be held in pooled wallets with
                internal ledgers tracking Client balances.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                9. Prohibited Activities
              </Typography>
              <Typography variant='body1'>Clients must not:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Provide false information
                </Typography>
                <Typography component='li' variant='body1'>
                  Use the Platform for unlawful purposes
                </Typography>
                <Typography component='li' variant='body1'>
                  Engage in money laundering or fraud
                </Typography>
                <Typography component='li' variant='body1'>
                  Circumvent geographic restrictions
                </Typography>
                <Typography component='li' variant='body1'>
                  Attempt unauthorized access
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                10. Limitation of Liability
              </Typography>
              <Typography variant='body1' paragraph>
                The Company is not liable for losses from market volatility,
                AI/algorithmic trading models, custodian failures, execution delays,
                system downtime, or unauthorized access caused by the Client. Total
                liability is limited to fees paid by the Client in the previous 12
                months.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                11. Governing Law & Dispute Resolution
              </Typography>
              <Typography variant='body1' paragraph>
                These Terms are governed by the laws of the Republic of Panama. Any
                dispute shall be resolved exclusively through binding arbitration in
                Panama City, Panama, in English.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant='h5' gutterBottom>
            Anti-Money Laundering & Know-Your-Customer (AML/KYC) Policy
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Governing Law: Panama (AML for investment services), United States
            (MSB/FinCEN), FATF global standards
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Purpose of the Policy
              </Typography>
              <Typography variant='body1'>This Policy aims to:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Prevent money laundering and terrorist financing
                </Typography>
                <Typography component='li' variant='body1'>
                  Detect and report suspicious activity
                </Typography>
                <Typography component='li' variant='body1'>
                  Ensure only legitimate users access the Platform
                </Typography>
                <Typography component='li' variant='body1'>
                  Comply with international AML/CTF regulations
                </Typography>
                <Typography component='li' variant='body1'>
                  Align with FATF recommendations and U.S. FinCEN MSB rules
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. AML & Compliance Responsibilities
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR CORP. (Panama):</strong> Responsible for trading
                activity, investment transactions, digital asset management, Trading
                Wallet monitoring, and client risk scoring.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR MSB INC. (Montana, USA):</strong> Responsible for fiat
                conversion, deposit/withdrawal monitoring, suspicious activity
                reporting (SAR), and U.S. federal MSB regulations.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR LAB LIMITED (Hong Kong):</strong> Provides
                technical enforcement including risk-based automation, transaction
                monitoring tooling, fraud-prevention systems, and sanctions screening
                APIs.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Prohibited Jurisdictions
              </Typography>
              <Typography variant='body1' paragraph>
                The Company does not onboard Clients from: FATF blacklisted countries
                (e.g., North Korea, Iran), OFAC embargoed countries (e.g., Cuba,
                Syria), EU/UK sanctions listed jurisdictions, and countries with
                severe AML risk.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. KYC Requirements for Individuals
              </Typography>
              <Typography variant='body1'>Required information:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Full legal name, date of birth, residential address
                </Typography>
                <Typography component='li' variant='body1'>
                  Nationality & citizenship
                </Typography>
                <Typography component='li' variant='body1'>
                  Valid government ID (passport, national ID, driver license)
                </Typography>
                <Typography component='li' variant='body1'>
                  Selfie verification and proof of address
                </Typography>
                <Typography component='li' variant='body1'>
                  Source of funds and tax information (where required)
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Sanctions Screening & PEP Checks
              </Typography>
              <Typography variant='body1' paragraph>
                All Clients are screened against OFAC, UN sanctions, EU sanctions, UK
                sanctions, PEP (Politically Exposed Person) lists, Interpol
                databases, and blockchain analytics alerts. High-risk individuals may
                require Enhanced Due Diligence (EDD).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Suspicious Activity Reporting (SAR)
              </Typography>
              <Typography variant='body1' paragraph>
                The Company may file SARs when transactions lack clear economic
                purpose, users refuse AML questions, patterns match laundering
                typologies, or blockchain tracing indicates illicit sources. Clients
                are not notified when SARs are filed.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Data Retention
              </Typography>
              <Typography variant='body1' paragraph>
                KYC and AML data is retained for 5-10 years, depending on
                jurisdictional requirements.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Typography variant='h5' gutterBottom>
            Sanctions & Restricted Countries Policy
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Purpose
              </Typography>
              <Typography variant='body1' paragraph>
                The Company is committed to preventing prohibited persons or
                jurisdictions from accessing the Platform, complying with
                international sanctions, protecting the Platform from illegal
                activity, and enforcing global AML/CTF obligations.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Prohibited Jurisdictions (No Access)
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>FATF Blacklisted Countries:</strong> North Korea, Iran,
                Myanmar (if blacklisted)
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>OFAC Embargoed Countries:</strong> Cuba, Syria, Iran, North
                Korea, Crimea, Donetsk, Luhansk
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>High AML/CTF Risk:</strong> Afghanistan, Yemen, Sudan,
                Somalia, South Sudan, Central African Republic, Eritrea
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Partial Restriction Countries
              </Typography>
              <Typography variant='body1' paragraph>
                Countries with limited access or enhanced due diligence requirements
                include: Russia, Belarus, Nigeria, Turkey, Vietnam, Pakistan. These
                may face additional compliance requirements.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Sanctions Screening Requirements
              </Typography>
              <Typography variant='body1' paragraph>
                Mandatory screening against OFAC SDN List, UN Security Council
                Sanctions, EU/UK sanctions lists, and blockchain intelligence risk
                indicators. VPN usage, proxy connections, or falsified documents
                result in immediate termination.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Geographical Restriction Enforcement
              </Typography>
              <Typography variant='body1'>
                Systems used to enforce restrictions:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  IP geolocation and device fingerprinting
                </Typography>
                <Typography component='li' variant='body1'>
                  Blockchain analytics and VPN detection
                </Typography>
                <Typography component='li' variant='body1'>
                  Withdrawal pattern monitoring
                </Typography>
                <Typography component='li' variant='body1'>
                  Sanctions screening and automated risk scoring
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Typography variant='h5' gutterBottom>
            Payment Services Agreement (MSB)
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Service Provider: POLAR MSB INC. (Montana, USA) | Governing Law: United
            States Federal Law & State of Montana Law
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Purpose
              </Typography>
              <Typography variant='body1'>This Agreement covers:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Crypto deposit and withdrawal processing
                </Typography>
                <Typography component='li' variant='body1'>
                  Fiat conversion (where applicable)
                </Typography>
                <Typography component='li' variant='body1'>
                  Transaction monitoring and KYC/AML compliance
                </Typography>
                <Typography component='li' variant='body1'>
                  Fraud and sanctions screening
                </Typography>
              </Box>
              <Typography variant='body1' paragraph>
                The MSB provides payment handling only, not investment services.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Eligibility
              </Typography>
              <Typography variant='body1' paragraph>
                Clients must be over 18, complete KYC verification, pass sanctions
                and AML checks, not be in restricted jurisdictions, and submit
                truthful information.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Services Provided
              </Typography>
              <Typography variant='body1' paragraph>
                Crypto deposit/withdrawal services, fiat on/off-ramp (if available),
                transaction monitoring, KYC & identity verification, and sanctions &
                fraud screening.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Prohibited Activities
              </Typography>
              <Typography variant='body1'>The MSB prohibits:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Chargeback fraud and use of stolen funds
                </Typography>
                <Typography component='li' variant='body1'>
                  Sanctioned-wallet deposits
                </Typography>
                <Typography component='li' variant='body1'>
                  Mixer/tumbler usage and darknet marketplace interactions
                </Typography>
                <Typography component='li' variant='body1'>
                  VPN masking during verification
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. AML/KYC Compliance
              </Typography>
              <Typography variant='body1' paragraph>
                The MSB must follow FinCEN AML Rules, OFAC sanctions, and Bank
                Secrecy Act reporting. Ongoing monitoring includes sanctions checks,
                blockchain provenance checks, and fraud pattern analysis. The MSB may
                file SARs/STRs without notification.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Deposits & Withdrawals
              </Typography>
              <Typography variant='body1' paragraph>
                Supported assets: BTC, USDT, USDC, ETH, SOL. Deposits require
                blockchain confirmations, AML origin checks, and wallet risk
                assessment. Withdrawals require complete KYC, sanctions screening,
                and verification of withdrawal address. Funds may be held for
                high-risk behavior or AML investigations.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Liability Limitations
              </Typography>
              <Typography variant='body1' paragraph>
                The MSB is not liable for trading losses, price fluctuations,
                incorrect wallet addresses, lost private keys, custodian downtime,
                regulatory freezes, or indirect damages. Maximum liability is MSB
                fees paid in the previous 12 months.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                8. Governing Law & Dispute Resolution
              </Typography>
              <Typography variant='body1' paragraph>
                Governed by U.S. federal law and State of Montana law. Disputes
                resolved by binding arbitration in Montana, in English.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={4}>
          <Typography variant='h5' gutterBottom>
            Risk Disclosure Statement
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Governing Law: Republic of Panama | Issued by: POLAR TENSOR CORP.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. No Guarantees & High-Risk Nature
              </Typography>
              <Typography variant='body1' paragraph>
                Trading digital assets is highly speculative and involves substantial
                risk of loss. The Client may lose all or part of their Digital
                Assets. Markets are volatile and may behave unexpectedly. Historical
                returns do not guarantee future results. There are no guarantees of
                profit, income, stability, accuracy, or performance.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Market Risk
              </Typography>
              <Typography variant='body1' paragraph>
                Prices may fluctuate sharply without warning. Markets may gap, crash,
                or move against positions instantly. Liquidity may disappear
                temporarily or permanently. The Company cannot control market
                conditions.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Algorithmic/AI Trading Risk (Polar One)
              </Typography>
              <Typography variant='body1' paragraph>
                Polar One uses machine learning models, neural networks, and
                high-frequency execution. AI models may misinterpret data, generate
                inaccurate predictions, underperform, fail during extreme market
                conditions, or degrade over time. Models may be retrained without
                notification, and systems may produce losing periods or drawdowns.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Execution Risk
              </Typography>
              <Typography variant='body1' paragraph>
                High-frequency trading requires reliable infrastructure. Execution
                may be delayed due to exchange congestion, volatility, API limits,
                network latency, or system overload. Delayed or failed orders may
                result in significant losses.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Custodial Risk
              </Typography>
              <Typography variant='body1'>
                Assets may be held in pooled omnibus custodial wallets. Risks
                include:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Custodian insolvency or operational failure
                </Typography>
                <Typography component='li' variant='body1'>
                  Hacks or security breaches
                </Typography>
                <Typography component='li' variant='body1'>
                  Asset freezes and wallet unavailability
                </Typography>
                <Typography component='li' variant='body1'>
                  Regulatory action against custodian
                </Typography>
                <Typography component='li' variant='body1'>
                  Withdrawal delays or suspensions
                </Typography>
              </Box>
              <Typography variant='body1' paragraph>
                The Client acknowledges the Company is not liable for custodian
                failures.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Technology Risk
              </Typography>
              <Typography variant='body1' paragraph>
                The Platform and trading systems may experience bugs, downtime, DDOS
                attacks, outages, failed updates, hardware failure, software
                vulnerabilities, or cloud provider issues. Technology risk may lead
                to loss of access, delayed execution, or loss of assets.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Withdrawal Risk
              </Typography>
              <Typography variant='body1' paragraph>
                Withdrawals may be delayed due to AML/KYC verification, risk alerts,
                custodian downtime, market conditions, or regulatory requests.
                Withdrawals may be permanently blocked if required by law.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                8. No Insurance / No Guarantee
              </Typography>
              <Typography variant='body1' paragraph>
                Digital assets are not insured. The Platform does not offer deposit
                insurance, government-backed guarantees, capital protection,
                performance guarantees, or loss reimbursement.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                9. Client Acknowledgement
              </Typography>
              <Typography variant='body1' paragraph>
                By using the Platform, the Client confirms they fully understand the
                risks, accept the possibility of losing all invested assets, have the
                financial capacity to bear such risks, enter voluntarily without
                solicitation, and have read this Disclosure in full.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={5}>
          <Typography variant='h5' gutterBottom>
            Algorithmic & AI Trading Consent Agreement
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Consent to Algorithmic Trading
              </Typography>
              <Typography variant='body1' paragraph>
                By activating the Trading Wallet, the Client authorizes the Company
                to execute trades automatically without prior notification, use
                machine learning and AI, adjust orders based on model signals,
                determine timing and order types, rebalance or liquidate positions,
                and retain profits. All trades are executed autonomously without
                manual approval.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Nature of Polar One System
              </Typography>
              <Typography variant='body1' paragraph>
                Polar One utilizes multi-timeframe analysis, neural networks, machine
                learning algorithms, pattern recognition, high-frequency trading
                logic, real-time orderbook analysis, ensemble model evaluations, and
                confidence scoring. Model architectures may change without notice,
                are proprietary, and may be retrained regularly. The system is not
                guaranteed to function optimally or profitably.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. AI Risks Acknowledgement
              </Typography>
              <Typography variant='body1'>
                The Client understands AI systems may:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Generate incorrect predictions
                </Typography>
                <Typography component='li' variant='body1'>
                  Fail during unexpected market conditions
                </Typography>
                <Typography component='li' variant='body1'>
                  Suffer model drift or degradation
                </Typography>
                <Typography component='li' variant='body1'>
                  Encounter data feed interruptions
                </Typography>
                <Typography component='li' variant='body1'>
                  Produce losing periods or extended drawdowns
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. High-Frequency Execution Consent
              </Typography>
              <Typography variant='body1' paragraph>
                The Client consents to sub-second execution, rapid trade
                entries/exits, multiple trades within seconds, position sizing
                determined by the system, and partial/full liquidation during extreme
                conditions. This may lead to increased transaction fees, slippage,
                performance volatility, and exposure to exchange latency.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. No Financial Advice
              </Typography>
              <Typography variant='body1' paragraph>
                The Company does NOT provide financial advice, evaluate personal
                financial situations, advise on suitability or risk tolerance, or
                recommend specific investment behavior. The AI system does NOT tailor
                strategies to individual clients or consider personal goals. It
                operates according to unified algorithmic logic.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Liability Waiver
              </Typography>
              <Typography variant='body1' paragraph>
                The Company is not liable for losses related to AI/algorithms, model
                performance, system errors, data misinterpretation, latency, outages,
                exchange failures, or any indirect/consequential damages.
                Client&apos;s sole remedy is termination of Trading Wallet use.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Revocation of Consent
              </Typography>
              <Typography variant='body1' paragraph>
                The Client may revoke consent by disabling Trading Wallet access,
                withdrawing assets (subject to fees), or closing their account.
                Revocation does not affect trades already executed.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={6}>
          <Typography variant='h5' gutterBottom>
            Fee & Tier Schedule
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Performance Fee Tiers
              </Typography>
              <Typography variant='body1' paragraph>
                Performance Fees are charged on net realized profits using High-Water
                Mark methodology:
              </Typography>
              <Box
                component='table'
                sx={{ width: '100%', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <Box
                      component='th'
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        p: 1,
                        textAlign: 'left',
                      }}
                    >
                      <Typography variant='body2' fontWeight='bold'>
                        Tier
                      </Typography>
                    </Box>
                    <Box
                      component='th'
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        p: 1,
                        textAlign: 'left',
                      }}
                    >
                      <Typography variant='body2' fontWeight='bold'>
                        Min. Balance (USDT)
                      </Typography>
                    </Box>
                    <Box
                      component='th'
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        p: 1,
                        textAlign: 'left',
                      }}
                    >
                      <Typography variant='body2' fontWeight='bold'>
                        Performance Fee
                      </Typography>
                    </Box>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Starter</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>50</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>50%</Typography>
                    </Box>
                  </tr>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Basic</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>500</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>40%</Typography>
                    </Box>
                  </tr>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Advanced</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>2,000</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>35%</Typography>
                    </Box>
                  </tr>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Professional</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>8,000</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>30%</Typography>
                    </Box>
                  </tr>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Elite</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>25,000</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>25%</Typography>
                    </Box>
                  </tr>
                  <tr>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>Enterprise</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>100,000</Typography>
                    </Box>
                    <Box
                      component='td'
                      sx={{ border: 1, borderColor: 'divider', p: 1 }}
                    >
                      <Typography variant='body2'>20%</Typography>
                    </Box>
                  </tr>
                </tbody>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Performance Fee Calculation
              </Typography>
              <Typography variant='body1' paragraph>
                Performance Fees use High-Water Mark (HWM) methodology. The Company
                tracks the highest net Trading Wallet value after fees. Fees apply
                only on new net profits exceeding the HWM. Deposits increase the HWM;
                withdrawals adjust it proportionally. Fees apply only to realized
                profits (closed trades), not unrealized gains.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Withdrawal Fee
              </Typography>
              <Typography variant='body1' paragraph>
                Capital = Initial Deposits + Reinvested Profits. A withdrawal fee
                applies when withdrawing:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  0-12 months after deposit: 10%
                </Typography>
                <Typography component='li' variant='body1'>
                  12-24 months after deposit: 5%
                </Typography>
                <Typography component='li' variant='body1'>
                  24+ months after deposit: free of charge
                </Typography>
              </Box>
              <Typography variant='body1' paragraph>
                Withdrawals of net, unreinvested profits are NOT subject to the fee.
                Withdrawals are processed in order: (1) Pure Net Profits (no fee),
                (2) Reinvested Profits (fee applies), (3) Initial Principal (fee
                applies).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Other Fees
              </Typography>
              <Typography variant='body1' paragraph>
                Deposits may incur blockchain fees. Withdrawals incur blockchain
                network fees, custodian fees, and applicable withdrawal fees.
                Internal transfers in USDT may have small processing fees.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={7}>
          <Typography variant='h5' gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Governing Law: Republic of Panama, US federal & Montana law, GDPR, Hong
            Kong law
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Data Controllers & Processors
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR CORP. (Panama):</strong> Data controller for
                investment and trading data.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR MSB INC. (Montana, USA):</strong> Data controller for
                payment processing, KYC/AML checks, and US compliance.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>POLAR TENSOR LAB LIMITED (Hong Kong):</strong> Data processor
                for platform hosting, software development, and technical
                maintenance.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Information We Collect
              </Typography>
              <Typography variant='body1'>We collect:</Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  <strong>Identity Data:</strong> Name, DOB, nationality, ID
                  documents, proof of address, tax IDs
                </Typography>
                <Typography component='li' variant='body1'>
                  <strong>Compliance Data:</strong> Sanctions checks, PEP screenings,
                  blockchain analytics, source of funds
                </Typography>
                <Typography component='li' variant='body1'>
                  <strong>Financial Data:</strong> Deposits, withdrawals, wallet
                  addresses, balances, fees, ledger entries
                </Typography>
                <Typography component='li' variant='body1'>
                  <strong>Technical Data:</strong> IP address, device identifiers,
                  browser info, activity logs, cookies
                </Typography>
                <Typography component='li' variant='body1'>
                  <strong>Communication Data:</strong> Support tickets, emails, chat
                  logs, submitted documents
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. How We Use Your Data
              </Typography>
              <Typography variant='body1' paragraph>
                To provide services (operate account, enable transactions, process
                payments, provide trading), comply with legal obligations (AML/KYC,
                sanctions, fraud prevention, reporting), improve the platform
                (optimize models, analyze usage, troubleshooting), protect the
                business (detect security incidents, respond to regulators, enforce
                terms), and communicate (account notifications, system updates,
                security alerts).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Legal Basis for Processing (GDPR)
              </Typography>
              <Typography variant='body1' paragraph>
                Contractual Necessity (fulfill obligations), Legitimate Interest
                (fraud prevention, optimization, security), Legal Obligation (AML,
                sanctions, compliance), and Consent (optional functions like
                cookies).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Data Sharing
              </Typography>
              <Typography variant='body1' paragraph>
                We may share data with affiliates, third-party service providers (KYC
                verification, blockchain analytics, custodians, payment processors,
                cloud hosting, legal consultants, security auditors), legal &
                regulatory authorities (AML/CTF compliance, subpoenas, sanctions
                enforcement), and as part of business operations (reorganization,
                merger, acquisition).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. International Data Transfers
              </Typography>
              <Typography variant='body1' paragraph>
                Data may be transferred to Panama, United States, Hong Kong, European
                Union, and global cloud hosting locations. Transfers comply with GDPR
                adequacy principles, Standard Contractual Clauses (SCCs), and local
                laws.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Data Retention
              </Typography>
              <Typography variant='body1' paragraph>
                KYC records: 5-10 years. Transaction records: 5-7 years. Support
                communication: 2-5 years. General account data: until closure, then
                archived per law.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                8. Data Security
              </Typography>
              <Typography variant='body1' paragraph>
                We implement encryption in transit and at rest, multi-factor
                authentication, firewalls and intrusion detection, segregated access
                control, secure data centers, and ongoing security audits. However,
                no system is fully secure. The Client accepts risk of cyberattacks
                and data breaches.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                9. Client Rights (GDPR)
              </Typography>
              <Typography variant='body1' paragraph>
                Clients may request access, correction, deletion (where legally
                allowed), restriction of processing, data portability, objection to
                processing, and withdrawal of consent. Requests may be denied for
                AML/CTF obligations, fraud prevention, legal compliance, or
                contractual requirements.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                10. Contact Information
              </Typography>
              <Typography variant='body1' paragraph>
                For privacy requests: POLAR TENSOR CORP., Compliance Department,
                compliance@polar-tensor.io, Republic of Panama.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={8}>
          <Typography variant='h5' gutterBottom>
            Master Client Investment Agreement
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Governing Law: Republic of Panama | Trading Mandate, Omnibus Custody
            Structure
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Purpose
              </Typography>
              <Typography variant='body1' paragraph>
                The Client appoints the Company to provide discretionary digital
                asset trading services using the Polar One automated trading engine.
                The Company accepts and agrees to manage the Client&apos;s Trading
                Wallet on the terms herein.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Representations by Client
              </Typography>
              <Typography variant='body1' paragraph>
                The Client represents they are 18+, act on their own initiative
                (reverse solicitation), understand volatility and risk of loss, no
                returns are guaranteed, provide lawful verified funds, are not in
                restricted jurisdictions or on sanctions lists, are beneficial owner
                of assets, understand AI/algorithmic trading risks, and are not
                relying on marketing or financial advice.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Grant of Discretionary Trading Authority
              </Typography>
              <Typography variant='body1' paragraph>
                The Client grants the Company full discretionary authority to execute
                trades automatically without notification, use AI-driven strategies,
                adjust orders based on model signals, determine timing/size/type of
                orders, rebalance or liquidate positions, and retain profits. Trading
                may occur at high frequency using machine learning, pattern
                recognition, and real-time orderbook analysis. The Company may update
                Polar One at any time.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Custody (Omnibus Structure)
              </Typography>
              <Typography variant='body1' paragraph>
                Assets may be held in pooled wallets at Binance or another custodian.
                The Company maintains internal ledger accounting. The Client
                acknowledges custodians operate independently; downtime or failure
                may cause delays/losses; omnibus wallets do not segregate on-chain;
                the Company is not liable for custodian insolvency, hacks, or
                failures; and withdrawals may be delayed due to custodian
                restrictions.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Fees and Charges
              </Typography>
              <Typography variant='body1' paragraph>
                Performance Fees per Fee & Tier Schedule using high-water-mark
                methodology. Withdrawal fees (0-12 months: 10%, 12-24 months: 5%, 24+
                months: free of charge) apply to principal and reinvested profits.
                Net profits not reinvested may be withdrawn without fees. Client pays
                blockchain, custodian, and internal transfer fees.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. Risk Acknowledgement
              </Typography>
              <Typography variant='body1' paragraph>
                The Client acknowledges: digital assets are highly volatile; markets
                may move suddenly; AI systems may fail or misinterpret signals;
                trading may suffer slippage, latency, execution errors; custodian may
                experience downtime; Client may lose 100% of assets; Company provides
                no insurance; strategies may produce extended drawdowns; there are no
                guaranteed returns.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Non-Fiduciary Status
              </Typography>
              <Typography variant='body1' paragraph>
                The Company is not a fiduciary. Does not owe suitability obligations,
                financial advice, or best execution obligations similar to licensed
                brokers. All trading decisions are executed as part of an automated
                system.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                8. Liability Limitation
              </Typography>
              <Typography variant='body1' paragraph>
                The Company is not liable for market losses, custodian failures,
                exchange outages, AI/algorithmic errors, network delays, force
                majeure events, third-party actions, or indirect/consequential
                damages.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                9. Term & Termination
              </Typography>
              <Typography variant='body1' paragraph>
                This Agreement remains in effect until terminated. The Company may
                terminate immediately for suspicious activity, breach of terms, legal
                obligations, sanctions, or incomplete KYC. Upon termination, assets
                will be returned when legally permissible.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                10. Dispute Resolution
              </Typography>
              <Typography variant='body1' paragraph>
                Governed by laws of Republic of Panama. Disputes resolved exclusively
                by binding arbitration in Panama City, Panama, in English.
                Arbitration awards are final.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={value} index={9}>
          <Typography variant='h5' gutterBottom>
            Custody & Third-Party Risk Agreement
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Custodians: Binance (primary) and other VASPs selected at Company
            discretion
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                1. Custody Structure
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>Omnibus Custody Model:</strong> Digital Assets are held in
                pooled omnibus wallets at third-party custodians holding assets for
                multiple Clients. The Company maintains internal ledger entries
                tracking each Client&apos;s balance. Blockchain-level segregation of
                Client assets does not exist. This is the chosen custody method.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>Custodians Are Independent:</strong> Custodians are
                independent, not owned by the Company, outside Company control, and
                free to impose their own limits. The Company does not guarantee
                custodian solvency, performance, or operational stability.
              </Typography>
              <Typography variant='body1' paragraph>
                <strong>Rights to Change Custodians:</strong> The Company may
                add/replace custodians, use multiple custodians, redistribute assets,
                or switch custodians without notice.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                2. Third-Party Risk
              </Typography>
              <Typography variant='body1'>
                The Client acknowledges and accepts full risk related to:
              </Typography>
              <Box component='ul' pl={3}>
                <Typography component='li' variant='body1'>
                  Technical outages, wallet downtime, API failures
                </Typography>
                <Typography component='li' variant='body1'>
                  Hacks, exploits, or security breaches
                </Typography>
                <Typography component='li' variant='body1'>
                  Insolvency, bankruptcy, or restructuring
                </Typography>
                <Typography component='li' variant='body1'>
                  Frozen or seized assets, withdrawal restrictions
                </Typography>
                <Typography component='li' variant='body1'>
                  Operational failures, regulatory enforcement
                </Typography>
                <Typography component='li' variant='body1'>
                  Liquidity shortages, transaction delays
                </Typography>
              </Box>
              <Typography variant='body1' paragraph>
                The Company is NOT the custodian, does NOT hold private keys, does
                NOT control custodian security, and is NOT responsible for losses
                caused by any custodian. The Company bears no liability for
                partial/total loss, inaccessible assets, slowed/halted withdrawals,
                price impact, or technical malfunction at custodian. The Client
                accepts this risk entirely.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                3. Execution & Liquidity Dependency
              </Typography>
              <Typography variant='body1' paragraph>
                All trades via Polar One depend on exchange uptime, liquidity
                availability, order-book depth, matching engine performance, and
                stable API connections. The Company cannot guarantee execution
                quality or availability. Delays or failures may occur due to
                custodian overload, network congestion, maintenance, volatile
                markets, API throttling, or wallet unavailability. The Company has
                zero liability for losses caused by these events.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                4. Wallet & Withdrawal Restrictions
              </Typography>
              <Typography variant='body1' paragraph>
                Withdrawal speed, limits, daily caps, and frozen assets are
                controlled by the custodian, not the Company. Custodians may freeze
                assets due to compliance checks, chain analysis alerts, suspicious
                behavior, or regulatory instructions. The Company must comply. The
                Company may impose withdrawal restrictions due to AML/KYC flags,
                custodian issues, risk alerts, legal obligations, security concerns,
                or operational requirements.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                5. Loss Allocation Clause
              </Typography>
              <Typography variant='body1' paragraph>
                The Client expressly agrees: Any loss, partial loss, inaccessibility,
                or seizure of Digital Assets held at a custodian is fully at the
                Client&apos;s risk and cost. The Company bears no responsibility or
                liability.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                6. No Claims Against the Company
              </Typography>
              <Typography variant='body1' paragraph>
                The Client waives all claims against the Company relating to
                custodian security, solvency, loss of funds, withdrawal delays,
                liquidity issues, exchange failures, or chain halts/forks. All such
                risks are assumed by the Client.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant='h6' gutterBottom>
                7. Client Confirmation
              </Typography>
              <Typography variant='body1' paragraph>
                By using the Platform, the Client confirms they have read and
                understood this Agreement, accept all third-party custodian risks,
                understand omnibus custody structure, waive claims against the
                Company for custodian failures, and provide informed, voluntary
                consent.
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    </Container>
  )
}
